import { Component, Input, SimpleChanges } from '@angular/core';
import { Shipment } from '@features/shared-features/models/shipment.model';
import { ShipmentHistoryItem } from '@features/shipments/models/shipment-history-item.model';
import { UserType } from '@shared-features/enums/shared-enums.enum';
import { ShipmentAction } from '@shared-features/enums/shipments.enum';
import { getDurationInDetails } from '@shared/utils/duration.util';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { TimelineItem } from '@shared/components/timeline/timeline-item.model';
import { IconType } from '@shared/enums';
import { ShipmentHeadedStatus } from '@shared/enums/shipment-headed-status';
import { takeUntil } from 'rxjs/operators';

// A run of destination-order history, grouped under its order and rendered via app-gate-to-gate-timeline.
interface GateHistoryGroup {
  order: NonNullable<ShipmentHistoryItem['order']>;
  history: ShipmentHistoryItem[];
}
type GateHistoryEntry = ShipmentHistoryItem | GateHistoryGroup;

// Steps whose AVL device-distance (when present) is worth surfacing as a flag.
const DEVICE_DISTANCE_ACTIONS = [
  ShipmentAction.AssignDriver,
  ShipmentAction.AcceptDriver,
  ShipmentAction.Start,
  ShipmentAction.ArrivePickupLocation,
  ShipmentAction.GoodsLoaded,
  ShipmentAction.ArriveDropOffLocation,
  ShipmentAction.GoodsDelivered,
];

// Figma's action glyph belongs beside the label, independently of the state marker.
const STEP_CONTENT_ICON: Partial<Record<ShipmentAction, string>> = {
  [ShipmentAction.Create]: 'outline-file',
  [ShipmentAction.AssignFleet]: 'status-truck',
  [ShipmentAction.ReassignFleet]: 'status-truck',
  [ShipmentAction.AcceptFleet]: 'status-truck',
  [ShipmentAction.AssignDriver]: 'status-driver',
  [ShipmentAction.AcceptDriver]: 'status-driver',
  [ShipmentAction.Start]: 'status-started',
  [ShipmentAction.ArrivePickupLocation]: 'status-location',
  [ShipmentAction.GoodsLoaded]: 'status-loaded',
  [ShipmentAction.ArriveDropOffLocation]: 'status-location',
  [ShipmentAction.GoodsDelivered]: 'status-delivered',
  [ShipmentAction.Cancel]: 'outline-close-circle',
};

// Presentation-only milestones: never inserted into shipment.history or destination groups.
const TIMELINE_MILESTONES = [
  ShipmentAction.Create, ShipmentAction.AssignFleet, ShipmentAction.AcceptFleet,
  ShipmentAction.AssignDriver, ShipmentAction.Start, ShipmentAction.ArrivePickupLocation,
  ShipmentAction.GoodsLoaded, ShipmentAction.ArriveDropOffLocation, ShipmentAction.GoodsDelivered,
];
const TERMINAL_ACTIONS = [
  ShipmentAction.GoodsDelivered, ShipmentAction.Cancel, ShipmentAction.NotFulfilled,
  ShipmentAction.Reject, ShipmentAction.Decline, ShipmentAction.Close,
];

@Component({
  selector: 'app-new-gate-to-gate',
  templateUrl: './new-gate-to-gate.component.html',
  styleUrls: ['./new-gate-to-gate.component.scss'],
})
export class NewGateToGateComponent extends BaseComponent {
  @Input() shipment: Shipment = new Shipment();
  ShipmentAction = ShipmentAction;
  readonly IconType = IconType;
  loadingOffTime: string;
  droppingOffTime: string;
  etaTime: any;
  formatedShipment: GateHistoryEntry[] = [];
  mainTimelineItems: TimelineItem[] = [];
  deliveredTimeline = false;

  constructor() {
    super();
    this.translationService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.buildMainTimelineItems();
    });
  }

  public get UserType(): typeof UserType {
    return UserType;
  }
  ngOnInit() {
    this.reformatShipmentHistory();
  }

  reformatShipmentHistory() {
    // check if shipment has order history and reformat the object to disply order history
    const orderMap = new Map<number, { index: number; history: ShipmentHistoryItem[]; order: GateHistoryGroup['order'] }>();
    const shipmentHistory: GateHistoryEntry[] = [];
    for (const [index, history] of this.shipment.history.entries()) {
      if (history.order) {
        if (orderMap.has(history.order.id)) {
          orderMap.get(history.order.id)!.history.push(history);
        } else {
          orderMap.set(history.order.id, { index, history: [history], order: history.order });
        }
      } else {
        shipmentHistory.push(history);
      }
    }

    orderMap.forEach((v, k) => {
      shipmentHistory.splice(v.index, 0, {
        order: v.order,
        history: v.history,
      });
    });
    this.formatedShipment = shipmentHistory;
    this.deliveredTimeline = this.shipment.history.some(item => !item.order && item.action === ShipmentAction.GoodsDelivered);
    this.buildMainTimelineItems();
  }

  timeConvert(hours: number): string {
    return this.translate('Shipments.Duration', getDurationInDetails((hours || 0) * 60 * 60 * 1000));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shipment && changes.shipment.currentValue) {
      const createdTime = this.shipment.history[0]?.createdAt || 0;
      const deliveredTime = this.shipment.history[this.shipment.history.length - 1]?.createdAt || 0;
      this.loadingOffTime = this.timeConvert(this.shipment?.loadingOffTime);
      this.droppingOffTime = this.timeConvert(this.shipment?.droppingOffTime);
      this.etaTime = getDurationInDetails(deliveredTime - createdTime);
      this.reformatShipmentHistory();
    }
  }

  /** Adapt the formatted history without moving or flattening destination groups. */
  private buildMainTimelineItems(): void {
    const entries = this.formatedShipment || [];
    const plainEntries = entries.filter((entry): entry is ShipmentHistoryItem => entry.order == null);
    const futureActions = this.getFutureActions();
    this.mainTimelineItems = entries.map((entry, index) => {
      if (entry.order != null) {
        const group = entry as GateHistoryGroup;
        return { label: group.order.dropOffCustomerTitle, group, groupIsLast: index === entries.length - 1, state: 'completed' };
      }
      const item = entry as ShipmentHistoryItem;
      return this.buildTimelineItem(
        item,
        !futureActions.length && !TERMINAL_ACTIONS.includes(item.action as ShipmentAction) && item === plainEntries[plainEntries.length - 1]
      );
    });
    this.mainTimelineItems.push(...futureActions.map((action, index): TimelineItem => ({
      label: this.translate('Enum.ShipmentStatus.' + action),
      state: index === 0 ? 'current' : 'pending',
      future: true,
      labelColor: 'var(--color-gray-600)',
      contentIcon: this.getContentIcon(action),
      contentIconType: IconType.SVG,
      contentIconSize: 'xs',
      contentIconColor: 'var(--color-accent)',
    })));
  }

  private getFutureActions(): ShipmentAction[] {
    const history = (this.shipment?.history || []).filter(item => !item.order);
    // ShipmentStatus and ShipmentAction are separate enums that share the same string values for
    // every terminal case (CANCELED, CLOSED, NOT_FULFILLED, REJECTED, GOODS_DELIVERED), so the
    // status compares correctly against TERMINAL_ACTIONS. The double cast is only there because
    // TypeScript will not widen between two unrelated enum types.
    if (!history.length || TERMINAL_ACTIONS.includes(this.shipment.status as unknown as ShipmentAction) ||
      TERMINAL_ACTIONS.includes(history[history.length - 1].action as ShipmentAction)) {
      return [];
    }
    const reached = history.map(item => {
      const action = item.action === ShipmentAction.ReassignFleet ? ShipmentAction.AssignFleet
        : item.action === ShipmentAction.AcceptDriver ? ShipmentAction.AssignDriver : item.action;
      return TIMELINE_MILESTONES.indexOf(action as ShipmentAction);
    });
    const lastReached = Math.max(...reached);
    return lastReached < 0 ? [] : TIMELINE_MILESTONES.slice(lastReached + 1);
  }

  private getContentIcon(action: ShipmentAction): string {
    return STEP_CONTENT_ICON[action] || 'status-timeline';
  }

  private buildTimelineItem(item: ShipmentHistoryItem, isLast: boolean): TimelineItem {
    const description: { text: string; color?: string }[] = [];

    if (item.action === ShipmentAction.ArrivePickupLocation) {
      const location = this.shipment?.pickUpLocation?.title || this.shipment?.pickUpLocation?.address;
      if (location) {
        description.push({ text: `${this.translate('sharedFeatures.Location')}: ${location}` });
      }
    }

    if (item.action === ShipmentAction.ArriveDropOffLocation) {
      const location = this.shipment?.dropOffLocation?.title || this.shipment?.dropOffLocation?.address;
      if (location) {
        description.push({ text: `${this.translate('sharedFeatures.Location')}: ${location}` });
      }
    }

    if (item?.avlDistance && DEVICE_DISTANCE_ACTIONS.includes(item.action as ShipmentAction)) {
      description.push({
        text: `${this.translate('sharedFeatures.GateDefinition.DeviceDistance')}: ${(+item.avlDistance).toFixed(2)} ${this.translate('Shipments.km')}`,
        color: 'var(--color-secondry)',
      });
    }

    if (item.action === ShipmentAction.AssignFleet || item.action === ShipmentAction.ReassignFleet) {
      const fleetName = item.fleet?.name || this.shipment?.fleet?.name;
      if (fleetName) {
        description.push({ text: `${this.translate('sharedFeatures.AssignedTo')}: ${fleetName}` });
      }
    }

    if (item.action === ShipmentAction.AcceptFleet) {
      const fleetName = item.fleet?.name || this.shipment?.fleet?.name;
      if (fleetName) {
        description.push({ text: `${this.translate('sharedFeatures.AcceptedBy')}: ${fleetName}` });
      }
    }

    if (item.action === ShipmentAction.AssignDriver || item.action === ShipmentAction.AcceptDriver) {
      const driverName = item.driver?.name || this.shipment?.driver?.fullName;
      const truckPlate = item.truck?.plateNumber || this.shipment?.truck?.plateNumber;
      if (driverName || truckPlate) {
        const parts = [
          driverName ? `${this.translate('sharedFeatures.Driver')}: ${driverName}` : '',
          truckPlate ? `${this.translate('sharedFeatures.Truck')}: ${truckPlate}` : '',
        ].filter(Boolean);
        description.push({ text: parts.join(' • ') });
      }
    }

    if (this.shipment?.loadingOffTime && item.action === ShipmentAction.ArrivePickupLocation) {
      description.push({
        text: `${this.translate('Shipments.WaitingTime')}: ${this.loadingOffTime}`,
        color: 'var(--color-warn)',
      });
    }

    if (this.shipment?.droppingOffTime && item.action === ShipmentAction.ArriveDropOffLocation) {
      description.push({
        text: `${this.translate('Shipments.WaitingTime')}: ${this.droppingOffTime}`,
        color: 'var(--color-warn)',
      });
    }

    if (
      item.headedStatus === ShipmentHeadedStatus.WrongHeaded &&
      (item.action === ShipmentAction.ArriveDropOffLocation || item.action === ShipmentAction.GoodsDelivered)
    ) {
      description.push({
        text: this.translate('shipments.ShipmentHeadedStatus.WRONG_HEADED'),
        color: 'var(--color-warn)',
      });
    }

    if (item.rejectionReason && TERMINAL_ACTIONS.includes(item.action as ShipmentAction) && item.action !== ShipmentAction.GoodsDelivered) {
      description.push({
        text: `${this.translate('shipments.Confirmation.Reason')}: ${item.rejectionReason}`,
        color: 'var(--color-warn)',
      });
    }

    return {
      label: this.buildLabel(item),
      createdAt: item.createdAt,
      state: isLast ? 'current' : 'completed',
      description,
      contentIcon: this.getContentIcon(item.action as ShipmentAction),
      contentIconType: IconType.SVG,
      contentIconSize: 'xs',
      contentIconColor: 'var(--color-accent)',
    };
  }

  private buildLabel(item: ShipmentHistoryItem): string {
    let label = this.translate('Enum.ShipmentStatus.' + item.action);

    if (
      (item.action === ShipmentAction.ArrivePickupLocation || item.action === ShipmentAction.ArriveDropOffLocation) &&
      item.isAutomatedMobile
    ) {
      label += ` (${this.translate('Enum.ShipmentStatus.AutomatedMobile')})`;
    }

    if (item.isAutomatedAVL) {
      label += ` (${this.translate('Enum.ShipmentStatus.Automated_AVL')})`;
    }

    if (item.action === ShipmentAction.GoodsDelivered && item.isAutomated) {
      label += ` — ${this.translate('sharedFeatures.MarkedAutoDelivered')} (${this.translate('sharedFeatures.BySystem')})`;
    }

    return label;
  }
}
